import { useEffect, useState, useRef } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion, useInView } from 'framer-motion';

const useCountUp = (end: number, duration: number, start: boolean) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [end, duration, start]);
  return count;
};

const StatsCounter = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const stats = t.stats.items;

  const count0 = useCountUp(stats[0].value, 1.5, isInView);
  const count1 = useCountUp(stats[1].value, 1.5, isInView);
  const count2 = useCountUp(stats[2].value, 1.5, isInView);

  const counts = [count0, count1, count2];

  return (
    <section ref={ref} className="py-16 md:py-24 px-4 border-y border-border/30">
      <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
        {stats.map((stat, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.2 }}
            className={`${i < stats.length - 1 ? 'sm:border-r sm:border-border/30' : ''}`}
          >
            <div className="text-5xl md:text-7xl font-bold text-primary drop-shadow-[0_0_15px_hsl(var(--primary)/0.3)]" style={{ fontFamily: 'Bebas Neue' }}>
              {counts[i]}{stat.suffix}
            </div>
            <p className="mt-2 text-muted-foreground uppercase tracking-widest text-sm">{stat.label}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default StatsCounter;
